import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import ConfirmDialog from "components/common/ConfirmDialog";
import SelectableCard from "./SelectableCard";
import EncounterSetupRows from "./EncounterSetupRows";
import { EVENT_TIME_FORMAT, INPATIENT_EVENT_OPTIONS, parseEventTime } from "./inpatient";

/** A fresh add form: the time starts at now, since an Events note is written as things happen. */
const blankEvent = () => ({ name: "", comment: "", occurredAt: format(new Date(), EVENT_TIME_FORMAT) });

/** The event and its comment, shared by the add form and the edit dialog. */
const EventFields = ({ control, autoFocus = false, commentRows = 2 }) => (
  <>
    <Controller
      name="name"
      control={control}
      render={({ field }) => (
        <Autocomplete
          freeSolo
          options={INPATIENT_EVENT_OPTIONS}
          value={field.value}
          inputValue={field.value}
          onChange={(_, value) => field.onChange(value || "")}
          onInputChange={(_, value) => field.onChange(value)}
          renderInput={(params) => (
            <TextField {...params} autoFocus={autoFocus} label="Event" placeholder="e.g., Fall, Hypoxia" />
          )}
        />
      )}
    />
    <Controller
      name="occurredAt"
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          type="datetime-local"
          label="Time"
          helperText="When it happened. Please change it if it was earlier."
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
      )}
    />
    <Controller
      name="comment"
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          label="Comment"
          placeholder="What happened and what was done"
          multiline
          minRows={commentRows}
          fullWidth
        />
      )}
    />
  </>
);

/**
 * Edit an event's name and comment, or remove it.
 * `event` is held by the caller until the dialog has finished closing (see EventsStep), so the
 * fields never empty out mid-fade.
 */
const EditEventDialog = ({ open, event, onClose, onExited, onSave, onRemove }) => {
  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: { name: "", comment: "", occurredAt: "" },
  });
  const name = watch("name");

  useEffect(() => {
    if (open && event) {
      reset({ name: event.name, comment: event.comment || "", occurredAt: event.occurredAt || "" });
    }
  }, [open, event, reset]);

  const submit = handleSubmit(({ name: value, comment, occurredAt }) => {
    onSave(event.id, { name: value.trim(), comment, occurredAt });
    onClose();
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth TransitionProps={{ onExited }}>
      <DialogTitle>Edit event</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <EventFields control={control} commentRows={3} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="error" onClick={() => onRemove(event)} sx={{ mr: "auto" }}>
          Remove
        </Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!String(name || "").trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * The Events note type's first step, in place of Record: an inline form to add an event, a card
 * per event with a pencil to edit it, and the same setup rows as Record so the note type can still
 * be changed. The events are the note's only source, so the differential, plan and note are all
 * written from them.
 */
const EventsStep = ({ events, onAddEvent, onUpdateEvent, onRemoveEvent, setup = null }) => {
  // Each dialog's event is kept until its close animation ends, and only `open` flips on close,
  // so neither dialog's content changes while it fades out.
  const [editing, setEditing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { dirtyFields },
  } = useForm({ defaultValues: blankEvent() });
  const name = watch("name");

  // Until the clinician sets the time themselves, it keeps up with the clock, so an event added a
  // while after the step opened is not charted at the time the step opened.
  const timeTouched = Boolean(dirtyFields.occurredAt);
  useEffect(() => {
    if (timeTouched) return undefined;
    const id = setInterval(() => setValue("occurredAt", format(new Date(), EVENT_TIME_FORMAT)), 15000);
    return () => clearInterval(id);
  }, [timeTouched, setValue]);

  const add = handleSubmit(({ name: value, comment, occurredAt }) => {
    if (!String(value || "").trim()) return;
    onAddEvent(value, comment, timeTouched ? occurredAt : format(new Date(), EVENT_TIME_FORMAT));
    reset(blankEvent());
  });

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 1.5 }}>
          Event
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please add the event this note is about, as it happens.
        </Typography>
      </Box>

      <Stack component="form" spacing={2} onSubmit={add} sx={{ mb: 4 }}>
        <EventFields control={control} />
        <Button
          type="submit"
          variant="contained"
          disabled={!String(name || "").trim()}
          sx={{ alignSelf: "flex-end" }}
        >
          Add Event
        </Button>
      </Stack>

      {events.map((event) => (
        <SelectableCard
          key={event.id}
          title={event.name}
          selectionVariant="none"
          onEdit={() => {
            setEditing(event);
            setEditOpen(true);
          }}
          editTooltip="Edit event"
          additionalContent={
            parseEventTime(event.occurredAt) || event.comment?.trim() ? (
              <>
                {parseEventTime(event.occurredAt) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {format(parseEventTime(event.occurredAt), "MMM d, HH:mm")}
                  </Typography>
                )}
                {event.comment?.trim() && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {event.comment}
                  </Typography>
                )}
              </>
            ) : null
          }
        />
      ))}

      {setup && (
        <Stack spacing={0.5} sx={{ mt: 4 }}>
          <EncounterSetupRows {...setup} />
        </Stack>
      )}

      <EditEventDialog
        open={editOpen}
        event={editing}
        onClose={() => setEditOpen(false)}
        onExited={() => setEditing(null)}
        onSave={onUpdateEvent}
        onRemove={(event) => {
          setEditOpen(false);
          setRemoving(event);
          setRemoveOpen(true);
        }}
      />

      <ConfirmDialog
        open={removeOpen}
        onClose={() => setRemoveOpen(false)}
        onExited={() => setRemoving(null)}
        title="Remove this event?"
        message={removing ? `${removing.name} and its comment will be removed from the note.` : ""}
        confirmLabel="Remove"
        confirmColor="error"
        onConfirm={() => {
          onRemoveEvent(removing.id);
          setRemoveOpen(false);
        }}
      />
    </Box>
  );
};

export default EventsStep;
